import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  ShoppingCart, 
  Package, 
  Pill, 
  ShoppingBag, 
  MapPin, 
  Plus, 
  Minus, 
  X 
} from 'lucide-react';

const CartDialog = ({
  open,
  onOpenChange,
  cart,
  supplementCart,
  consumablesCart,
  cartTotal,
  supplementCartTotal,
  consumablesCartTotal,
  grandTotal,
  ticketMediciel,
  setTicketMediciel,
  updateQuantity,
  updateSupplementQuantity,
  updateConsumableQuantity,
  removeFromCart,
  removeSupplementFromCart,
  removeConsumableFromCart,
  handleValidateSale,
  submitting,
  language
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            {language === 'fr' ? 'Panier' : 'Cart'}
          </DialogTitle>
        </DialogHeader>
        
        {cart.length === 0 && supplementCart.length === 0 && consumablesCart.length === 0 ? (
          <div className="text-center py-8">
            <ShoppingCart className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
            <p className="text-[#78716C]">
              {language === 'fr' ? 'Panier vide' : 'Empty cart'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Cosmetics Section */}
            {cart.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-[#78716C] mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  {language === 'fr' ? 'Cosmétiques' : 'Cosmetics'}
                </h4>
                <div className="space-y-2">
                  {cart.map(item => (
                    <div key={item.product_id} className="flex items-center gap-3 p-3 bg-[#F5F5F4] rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0F172A] text-sm truncate">{item.name}</p>
                        {item.emplacement && (
                          <p className="text-xs text-[#78716C] flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {item.emplacement}
                          </p>
                        )}
                        <p className="text-sm text-[#0EA5E9]">
                          {item.price.toLocaleString('fr-FR')} FCFA x {item.quantity}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, -1)}
                          className="w-7 h-7 rounded-full bg-white flex items-center justify-center"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, 1)}
                          className="w-7 h-7 rounded-full bg-white flex items-center justify-center"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.product_id)}
                          className="w-7 h-7 rounded-full bg-red-100 text-red-500 flex items-center justify-center ml-2"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-right mt-2 text-sm text-[#78716C]">
                  Sous-total: <span className="font-medium text-[#0F172A]">{cartTotal.toLocaleString('fr-FR')} FCFA</span>
                </div>
              </div>
            )}

            {/* Supplements Section */}
            {supplementCart.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-green-700 mb-2 flex items-center gap-2">
                  <Pill className="w-4 h-4" />
                  {language === 'fr' ? 'Compléments alimentaires' : 'Supplements'}
                </h4>
                <div className="space-y-2">
                  {supplementCart.map(item => (
                    <div key={item.item_id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0F172A] text-sm truncate">{item.name}</p>
                        <p className="text-xs text-green-600">
                          Cure: {item.cure_duration_days} jours
                        </p>
                        <p className="text-sm text-green-600">
                          {item.price.toLocaleString('fr-FR')} FCFA x {item.quantity}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateSupplementQuantity(item.item_id, -1)}
                          className="w-7 h-7 rounded-full bg-white flex items-center justify-center"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateSupplementQuantity(item.item_id, 1)}
                          className="w-7 h-7 rounded-full bg-white flex items-center justify-center"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => removeSupplementFromCart(item.item_id)}
                          className="w-7 h-7 rounded-full bg-red-100 text-red-500 flex items-center justify-center ml-2"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-right mt-2 text-sm text-green-700">
                  Sous-total: <span className="font-medium">{supplementCartTotal.toLocaleString('fr-FR')} FCFA</span>
                </div>
              </div>
            )}

            {/* Consumables Section */}
            {consumablesCart.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-orange-700 mb-2 flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4" />
                  {language === 'fr' ? 'Consommables' : 'Consumables'}
                </h4>
                <div className="space-y-2">
                  {consumablesCart.map(item => (
                    <div key={item.item_id} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-100">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0F172A] text-sm truncate">{item.name}</p>
                        <p className="text-sm text-orange-600">
                          {item.price.toLocaleString('fr-FR')} FCFA x {item.quantity}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateConsumableQuantity(item.item_id, -1)}
                          className="w-7 h-7 rounded-full bg-white flex items-center justify-center"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateConsumableQuantity(item.item_id, 1)}
                          className="w-7 h-7 rounded-full bg-white flex items-center justify-center"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => removeConsumableFromCart(item.item_id)}
                          className="w-7 h-7 rounded-full bg-red-100 text-red-500 flex items-center justify-center ml-2"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-right mt-2 text-sm text-orange-700">
                  Sous-total: <span className="font-medium">{consumablesCartTotal.toLocaleString('fr-FR')} FCFA</span>
                </div>
              </div>
            )}
          </div>
        )}
        
        {(cart.length > 0 || supplementCart.length > 0 || consumablesCart.length > 0) && (
          <DialogFooter className="flex-col gap-3 sm:flex-col">
            <div className="flex justify-between items-center w-full py-3 border-t">
              <span className="font-medium">{language === 'fr' ? 'Total' : 'Total'}</span>
              <span className="text-xl font-bold text-[#0F172A]">
                {grandTotal.toLocaleString('fr-FR')} FCFA
              </span>
            </div>
            
            {/* Ticket Mediciel Input in Dialog */}
            <div className="w-full">
              <Label htmlFor="ticket-mediciel-dialog" className="text-sm font-medium text-[#5D4E37]">
                N° Ticket Mediciel <span className="text-red-500">*</span>
              </Label>
              <Input
                id="ticket-mediciel-dialog"
                value={ticketMediciel}
                onChange={(e) => setTicketMediciel(e.target.value)}
                placeholder="Entrez le numéro de ticket Mediciel"
                className="mt-1 border-[#C9A086] focus:border-[#10B981]"
                data-testid="ticket-mediciel-dialog-input"
              />
            </div>
            
            <Button
              onClick={() => { onOpenChange(false); handleValidateSale(); }}
              disabled={submitting || !ticketMediciel.trim()}
              className="w-full bg-[#10B981] hover:bg-[#059669] text-white disabled:bg-gray-400"
            >
              {language === 'fr' ? 'Valider la vente' : 'Validate Sale'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CartDialog;
